"use client"

import { useEffect, useId, useSyncExternalStore } from "react"
import { useLocale } from "next-intl"
import { useCountry } from "@/components/layout/country-provider"
import { GiftIcon } from "@/components/ui/icons"
import { useTranslations } from "@/lib/intl/use-translations"
import { lpaGiftLastDay, lpaGiftRunning } from "@/lib/lpa-gift"
import { cn } from "@/lib/utils"

/** The `lpa_gift` kill switch, fetched once per page from /api/lpa-gift,
 *  and the offer's end date. The server's HTML always has the gift (the
 *  pages are built ahead), so hydration never disagrees with it; the page
 *  takes it away once it runs, if the flag says off or the offer is over. */
let flagOn = true
let flagAsked = false
const flagListeners = new Set<() => void>()
function subscribeFlag(listener: () => void) {
  flagListeners.add(listener)
  if (!flagAsked) {
    flagAsked = true
    fetch("/api/lpa-gift")
      .then((res) => (res.ok ? res.json() : null))
      .then((body: { enabled?: boolean } | null) => {
        if (body?.enabled === false) {
          flagOn = false
          flagListeners.forEach((l) => l())
        }
      })
      .catch(() => {})
  }
  return () => flagListeners.delete(listener)
}
const subscribeNothing = () => () => {}
/** Asked only where the gift can show (Switzerland), so other markets
 *  never call /api/lpa-gift. */
const useLpaGiftOn = (asked: boolean) =>
  useSyncExternalStore(
    asked ? subscribeFlag : subscribeNothing,
    () => flagOn && lpaGiftRunning(),
    () => true
  )

/**
 * The free Lp(a) test a new membership gets, in Switzerland only, until
 * LPA_GIFT_ENDS (AHD-14738): a yellow present and the line, on a soft
 * green band, marked "*" for the small print (LpaGiftNote) at the page's
 * foot. Elsewhere, later, or with the `lpa_gift` flag off (lib/lpa-gift.ts)
 * this returns nothing, so callers can drop it in without their own check.
 */
export function useLpaGift() {
  const { code: country } = useCountry()
  const locale = useLocale()
  const t = useTranslations("ui")
  const on = useLpaGiftOn(country === "ch")
  if (country !== "ch" || !on) return null
  return {
    line: t("lpaGiftLine"),
    pill: t("lpaGiftPill"),
    note: t("lpaGiftNote", { date: lpaGiftLastDay(locale) }),
    mark: t("lpaGiftMark"),
    included: t("lpaGiftIncluded"),
    includedNote: t("lpaGiftIncludedNote"),
    includedIn: t("lpaGiftIncludedIn"),
    // The Lp(a) and heart-health pages' words while the gift runs; their
    // own keys keep the add-on's for when it does not.
    heroCtaNote: t("lpaGiftHeroCtaNote"),
    stepsBody: t("lpaGiftStepsBody"),
    step1Body: t("lpaGiftStep1Body"),
    closingBody: t("lpaGiftClosingBody"),
    cta: t("lpaGiftCta"),
    heartMeasureBody: t("lpaGiftHeartMeasureBody"),
    heartMriTagline: t("lpaGiftHeartMriTagline"),
    heartBloodOnly: t("lpaGiftHeartBloodOnly"),
  }
}

export type LpaGift = NonNullable<ReturnType<typeof useLpaGift>>

/** The present, in the size of the lists' check circles, in the warm
 *  yellow: green hid it among the ticks, terracotta read as an alert
 *  (Eugene, Michèle, 04.11). */
export function LpaGiftMark({
  label,
  className,
}: {
  label: string
  className?: string
}) {
  return (
    <span
      role="img"
      aria-label={label}
      data-lpa-gift=""
      className={cn(
        "bg-apricot-200 text-apricot-900 inline-grid size-[22px] shrink-0 place-items-center rounded-full",
        className
      )}
    >
      <GiftIcon size={12} />
    </span>
  )
}

/** "Free with membership*" with the present, in the gift's yellow: under
 *  the Lp(a) test's own price on its page (Eugene, 03.11). The present
 *  bare, as on the booking's "Free": a yellow circle on yellow would
 *  vanish (Eugene, 04.11). */
export function LpaGiftPill({ text, label }: { text: string; label: string }) {
  return (
    <span
      data-lpa-gift=""
      className="type-body-md bg-apricot-200 text-apricot-900 inline-flex w-fit items-center gap-2 rounded-full py-1 pr-3.5 pl-3 font-medium"
    >
      <span role="img" aria-label={label} className="inline-grid">
        <GiftIcon size={14} />
      </span>
      {text}
    </span>
  )
}

/** Which FAQ's LpaGiftNote holds the small print: the first on the page
 *  (Eugene, 03.11: under the FAQ, not hidden). */
const noteHolders: string[] = []
const noteListeners = new Set<() => void>()
const noteChanged = () => noteListeners.forEach((l) => l())
function subscribeNote(listener: () => void) {
  noteListeners.add(listener)
  return () => noteListeners.delete(listener)
}

/**
 * The gift's small print ("*"), once per page, in the body's type and on
 * the left like the copy it explains. `place="faq"` under an FAQ's list;
 * `place="foot"` (the page shell) only where no FAQ holds it. Either shows
 * only on a page that says the "*": where the gift's copy renders it
 * carries `data-lpa-gift` (LpaGiftMark and the pages' starred lines), so a
 * blog post, a legal page or an FAQ elsewhere stays without it. CSS reads
 * that, so the server's HTML is right before the page runs.
 */
export function LpaGiftNote({
  place,
  className,
}: {
  place: "faq" | "foot"
  className?: string
}) {
  const gift = useLpaGift()
  const on = !!gift
  const id = useId()
  useEffect(() => {
    if (place !== "faq" || !on) return
    noteHolders.push(id)
    noteChanged()
    return () => {
      noteHolders.splice(noteHolders.indexOf(id), 1)
      noteChanged()
    }
  }, [place, on, id])
  const first = useSyncExternalStore(
    subscribeNote,
    () => place === "foot" || noteHolders[0] === id,
    () => true
  )
  if (!gift || !first) return null
  return (
    <p
      data-lpa-note={place}
      className={cn(
        "type-body-md hidden text-pretty text-neutral-600",
        place === "faq"
          ? "[body:has([data-lpa-gift])_&]:block"
          : "[body:has([data-lpa-gift]):not(:has([data-lpa-note=faq]))_&]:block",
        className
      )}
    >
      {gift.note}
    </p>
  )
}

/**
 * The gift as one row of the plan's checklist on the dark photo cards: the
 * same height and padding as the rows around it, with the band behind it.
 */
export function LpaGiftRow({
  gift,
  className,
}: {
  gift: LpaGift
  className?: string
}) {
  return (
    <div
      className={cn(
        "bg-deep-green-400/15 flex min-h-9 items-center gap-x-3 rounded-[18px] px-[11px] py-1.5",
        className
      )}
    >
      <LpaGiftMark label={gift.mark} />
      <span className="type-body-lg-emphasized text-balance text-white">
        {gift.line}
      </span>
    </div>
  )
}

/** The single test's key in the plan prices (`lib/pricing.ts`). */
export const LPA_GIFT_KEY = "addonLpa"
